/**
 * EL Nº DE REGISTRO QUE SE IMPRIME, RESUELTO EN UN SOLO SITIO (25-09-2026).
 *
 * Hasta hoy cada salida que enseñaba un nº de registro se hacía su propio
 * COALESCE —`get_consumption_report` (línea → producto), el escáner, el
 * asistente…— y ninguna miraba si lo que copiaba ERA un nº de registro. En
 * producción, de 725 líneas de aplicación con el nº guardado solo 198 eran
 * números reales del MAPA: 290 dicen «Es», 118 «Es172», 89 llevan «554» en
 * COSTAR (un 554 a secas no existe —el ES-00554 es DAGONIS—; COSTAR es el
 * 22060) y en BENEVIA hay un
 * lote (722261) guardado como nº. Imprimir eso en un registro de aplicaciones
 * que se le enseña a un inspector es peor que dejar el hueco: el hueco se ve y
 * se corrige; un nº falso con aspecto oficial se copia.
 *
 * LA REGLA, una sola para todas las salidas (PDF de aplicaciones, notas de
 * consumo, y el cuaderno y el asistente cuando lleguen):
 *
 *   1. el nº guardado en la línea (snapshot), si es VÁLIDO;
 *   2. si no, el nº que tiene HOY el producto en el inventario, si es VÁLIDO;
 *   3. si no, null —quien imprime pone «—»—, con avisos tipados de por qué.
 *
 * VÁLIDO quiere decir:
 *   - FORMATO: dígitos, con o sin «ES» delante (22839, ES-01405), y como mucho
 *     cinco significativos: el MAPA no pasa de 25973 ni de ES-02112, así que un
 *     lote de seis cifras no cabe. Ni «Es», ni «5L», ni el lote de la línea.
 *   - Y, SI llega el catálogo del MAPA (el de `useRegistryCatalog` /
 *     `phyto-registry-catalog`), que exista en él y que no sea el nº de OTRO
 *     producto (AGRIMEC con el 22649, que es SPIDERMEC). Sin catálogo —sin red,
 *     o porque nadie lo pidió— solo se mira el formato.
 *
 * Las empresas con registros de fuera (Chinampa Farms trabaja con RSCO de
 * México aunque figure como España, D26) apagan la validación con
 * `mapaValidation: false`: entonces vale cualquier código con dígitos que no
 * sea el lote ni una cantidad, y el catálogo no se mira.
 *
 * LA CLAVE (`registryKey`). El 25-09 el resolvedor se hizo su propia clave
 * porque `normalizeRegistration` quitaba el «ES» y los ceros y tiraba lo que se
 * quedaba en menos de tres caracteres: las 60 fichas VIGENTES de ES-00001 a
 * ES-00099 (ES-00015 AXIAL PRO, ES-00054 ULTRA-PROM…) no se podían imprimir
 * nunca. Desde el 26-09 (C1d revisado) las dos son LA MISMA: `registryKey`
 * llama a `normalizeRegistration`, que conserva el espacio de nombres —«ES» +
 * dígitos sin ceros para los ES (ES-00054 → «ES54», ES-01405 → «ES1405»),
 * dígitos solos para los nacionales (22839)—, y esa misma regla es la de
 * `registro_norm` de la copia del MAPA y la de SQL (`normalize_registration_number`).
 * Un 54 a secas NO es el ES-00054, ni el 554 de COSTAR es el ES-00554 de
 * DAGONIS: los nacionales del MAPA empiezan en el 11179, así que un nº corto sin
 * «ES» no existe.
 *
 * PURO a propósito: su único import es `normalizeRegistration`, que no importa
 * nada. Así `scripts/sync-registry-resolver.mjs` saca una copia literal para
 * las funciones de Supabase (`supabase/functions/_shared/registryResolve.ts`,
 * con el import cambiado a su gemela `./productMatch.ts`) y el cuaderno del
 * servidor imprime lo mismo que el PDF del navegador.
 */

import { normalizeRegistration } from "@/lib/productMatch";

/** De dónde salió el nº que se imprime. */
export type RegistrationSource = "snapshot" | "inventory";

/**
 * Por qué se descartó un candidato (o, `missing`, que no queda ninguno).
 * `not_current` es el único que NO descarta: el nº existe y es el suyo, pero el
 * MAPA lo tiene como no vigente; se imprime y quien quiera avisa.
 */
export type RegistrationWarning =
  | { kind: "bad_format"; source: RegistrationSource; raw: string }
  | { kind: "equals_batch"; source: RegistrationSource; raw: string }
  | { kind: "looks_like_quantity"; source: RegistrationSource; raw: string }
  | { kind: "not_in_registry"; source: RegistrationSource; raw: string }
  | { kind: "other_product"; source: RegistrationSource; raw: string; registryName: string }
  | { kind: "not_current"; source: RegistrationSource; raw: string }
  | { kind: "missing" };

/**
 * Lo mínimo de una ficha del MAPA para validar. `RegistryItem` (el del
 * catálogo del navegador) encaja tal cual; se declara aquí para no importarlo.
 */
export interface RegistryEntryLike {
  /** Nº tal y como lo publica el MAPA: '22839' | 'ES-01405'. */
  num: string;
  nombre?: string | null;
  vigente?: boolean | null;
}

/**
 * El catálogo, por la clave del resolvedor (`registryKey`: «ES54», «22839»). Un
 * `Set` solo dice «existe»; un `Map` (el de `buildRegistryIndex`) dice además
 * de QUÉ producto es, y con eso se cazan los nº de otro producto.
 */
export type RegistryIndex = ReadonlySet<string> | ReadonlyMap<string, RegistryEntryLike>;

export interface RegistrationInput {
  /** `spray_log_entries.registration_number_snapshot`: lo que se guardó al aplicar. */
  snapshot?: string | null;
  /** `inventory.registration_number` del producto, HOY (respaldo). */
  inventory?: string | null;
  /** Nombre del producto en la línea: para ver si el nº es el suyo. */
  productName?: string | null;
  /** Lote de la línea o del movimiento: un nº igual al lote es el lote mal puesto. */
  batch?: string | null;
}

export interface ResolveRegistrationOptions {
  /** Sin catálogo (null/undefined) solo se valida el formato. */
  catalog?: RegistryIndex | null;
  /** D26: `false` para empresas con registros que no son del MAPA. Por defecto, true. */
  mapaValidation?: boolean;
}

export interface RegistrationResolution {
  /** Lo que se imprime. null → `REGISTRATION_PLACEHOLDER`. */
  value: string | null;
  source: RegistrationSource | null;
  /** Nombre del producto en el MAPA cuando se validó contra un catálogo con nombres. */
  registryName: string | null;
  warnings: RegistrationWarning[];
}

/** Lo que se pinta en lugar de un nº que no pasa (D12: nunca el nº basura). */
export const REGISTRATION_PLACEHOLDER = "—";

/** Mayúsculas y solo letras y dígitos: «ES-01 405» → «ES01405». */
function compact(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** «5L», «1000 ml», «500 GRS.»: una cantidad con su unidad, no un registro. */
const QUANTITY_RE =
  /^\d+(?:[.,]\d+)?\s*(?:L|LT|LTS|LITROS?|ML|CC|KG|KGS|KILOS?|G|GR|GRS|GRAMOS?|UD|UDS|UNIDADES)\.?$/i;

/** El MAPA no pasa de cinco cifras significativas (25973, ES-02112). */
const MAX_SIGNIFICANT_DIGITS = 5;

/**
 * La clave con la que el resolvedor busca un nº en el catálogo: la de
 * `normalizeRegistration`, sin copia propia (ver la cabecera). «ES-00054» →
 * «ES54», «es 1405» → «ES1405», «22839» → «22839». null si no puede ser un nº:
 *   - sin ningún dígito («Es», «EEEE», «XXXXX»: lo que se teclea para pasar un
 *     campo obligatorio);
 *   - un ES que solo son ceros («ES-000000») o que detrás lleva letras («ES-A1»);
 *   - un nº sin «ES» de menos de tres caracteres significativos («001», «54»).
 */
export function registryKey(raw: string | null | undefined): string | null {
  return normalizeRegistration(raw);
}

type FormatProblem = "bad_format" | "equals_batch" | "looks_like_quantity";

function formatProblem(raw: string, batch: string | null | undefined, spanish: boolean): FormatProblem | null {
  const c = compact(raw);
  if (batch && compact(batch) && compact(batch) === c) return "equals_batch";
  if (QUANTITY_RE.test(raw.trim())) return "looks_like_quantity";
  if (registryKey(raw) == null) return "bad_format";
  if (spanish) {
    const digits = c.startsWith("ES") ? c.slice(2) : c;
    if (!/^\d+$/.test(digits)) return "bad_format";
    if (digits.replace(/^0+/, "").length > MAX_SIGNIFICANT_DIGITS) return "bad_format";
  }
  return null;
}

/**
 * Cómo se escribe un nº que el catálogo confirma pero sin darnos su ficha (un
 * `Set`): con «ES» → «ES-» y cinco cifras, como lo publica el MAPA; solo
 * dígitos → sin ceros delante.
 */
function canonical(raw: string): string {
  const c = compact(raw);
  if (c.startsWith("ES")) {
    const digits = c.slice(2).replace(/^0+/, "");
    return `ES-${digits.padStart(MAX_SIGNIFICANT_DIGITS, "0")}`;
  }
  return c.replace(/^0+/, "");
}

// ── ¿Es el nº de ESTE producto? ─────────────────────────────────────────────

/** Palabras que no identifican un producto (envase, unidades, reclamos). */
const NAME_NOISE = new Set([
  "ECO", "BIO", "PLUS", "TOP", "MAX", "GOLD", "PREMIUM", "EXTRA", "NEW", "PRO", "DUO", "FORTE", "ULTRA",
  "LTS", "LITRO", "LITROS", "KGS", "KILO", "KILOS", "GRS", "GRAMOS", "UDS", "UNIDADES", "BOTE", "SACO",
  "CON", "PARA", "DEL", "LOS", "LAS",
]);

function nameTokens(name: string): string[] {
  return name
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^A-Z0-9]+/)
    .filter((w) => w.length >= 3 && /^[A-Z]+$/.test(w) && !NAME_NOISE.has(w));
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length];
}

function similarWords(a: string, b: string): boolean {
  if (a === b) return true;
  const short = Math.min(a.length, b.length);
  // «DINAMIT» / «DINAMITE»: una empieza donde acaba la otra.
  if (short >= 4 && (a.startsWith(b) || b.startsWith(a))) return true;
  // Erratas de lectura con el nº bueno: KILEO/KYLEO (1), COSAYR/COSTAR (2).
  return levenshtein(a, b) <= (short >= 6 ? 2 : short >= 4 ? 1 : 0);
}

/**
 * ¿Puede este nombre de la línea ser el producto que el MAPA tiene con ese nº?
 *
 * Basta con UNA palabra en común (o casi): las facturas escriben «SPINTOR 480
 * SC (10X1) / 1 L. ECO (DOW) *» y el MAPA «SPINTOR 480 SC». Lo que se caza es
 * lo que no comparte nada: AGRIMEC/SPIDERMEC, TUTA FORT/BERMECTINE, NATEROX/
 * AFFIRM, COSTAR/DAGONIS, NETO STARTOSIL/MILSAJ, Cuprotec/KUPROS (los seis
 * cruzados del 25-09). Sin nombre por uno de los lados no se juzga: true.
 */
export function namesCompatible(productName: string | null | undefined, registryName: string | null | undefined): boolean {
  if (!productName || !registryName) return true;
  const mine = nameTokens(productName);
  const theirs = nameTokens(registryName);
  if (!mine.length || !theirs.length) return true;
  return mine.some((a) => theirs.some((b) => similarWords(a, b)));
}

// ── Catálogo ───────────────────────────────────────────────────────────────

/**
 * El índice del catálogo por `registryKey`, con los ES en su propio espacio
 * (ES-00054 → «ES54»), así que ninguna ficha se queda fuera y un ES no pisa a
 * un nacional. Si la misma clave llegara dos veces, gana la vigente.
 */
export function buildRegistryIndex(items: readonly RegistryEntryLike[]): Map<string, RegistryEntryLike> {
  const index = new Map<string, RegistryEntryLike>();
  for (const it of items) {
    const key = registryKey(it.num);
    if (!key) continue;
    const prev = index.get(key);
    if (!prev || (prev.vigente === false && it.vigente !== false)) index.set(key, it);
  }
  return index;
}

function lookup(catalog: RegistryIndex, key: string): RegistryEntryLike | "exists" | null {
  if (catalog instanceof Map) return (catalog as ReadonlyMap<string, RegistryEntryLike>).get(key) ?? null;
  return (catalog as ReadonlySet<string>).has(key) ? "exists" : null;
}

// ── La regla ───────────────────────────────────────────────────────────────

/**
 * Resuelve el nº de registro que se imprime para una línea. Ver la cabecera.
 */
export function resolveRegistration(
  input: RegistrationInput,
  options: ResolveRegistrationOptions = {},
): RegistrationResolution {
  const spanish = options.mapaValidation !== false;
  const catalog = spanish ? options.catalog ?? null : null;
  const warnings: RegistrationWarning[] = [];

  const candidates: [RegistrationSource, string | null | undefined][] = [
    ["snapshot", input.snapshot],
    ["inventory", input.inventory],
  ];

  for (const [source, rawValue] of candidates) {
    const raw = typeof rawValue === "string" ? rawValue.trim() : "";
    if (!raw) continue;

    const problem = formatProblem(raw, input.batch, spanish);
    if (problem) {
      warnings.push({ kind: problem, source, raw });
      continue;
    }

    if (!spanish) {
      return { value: raw.replace(/\s+/g, " "), source, registryName: null, warnings };
    }

    /* Sin catálogo se imprime TAL CUAL se escribió, sin ponerlo bonito: «Es172»
       pasa el formato, pero no se sabe si existe, y vestirlo de «ES-00172» le
       daría un aspecto oficial que nadie ha comprobado. */
    if (!catalog) {
      return { value: raw.replace(/\s+/g, " "), source, registryName: null, warnings };
    }

    const key = registryKey(raw) as string;
    const hit = lookup(catalog, key);
    if (!hit) {
      warnings.push({ kind: "not_in_registry", source, raw });
      continue;
    }
    if (hit === "exists") {
      return { value: canonical(raw), source, registryName: null, warnings };
    }
    const registryName = hit.nombre ?? null;
    if (registryName && !namesCompatible(input.productName, registryName)) {
      warnings.push({ kind: "other_product", source, raw, registryName });
      continue;
    }
    if (hit.vigente === false) warnings.push({ kind: "not_current", source, raw });
    return { value: hit.num, source, registryName, warnings };
  }

  warnings.push({ kind: "missing" });
  return { value: null, source: null, registryName: null, warnings };
}

/** Atajo para quien solo pinta: el nº válido o «—». */
export function registrationLabel(
  input: RegistrationInput,
  options: ResolveRegistrationOptions = {},
): string {
  return resolveRegistration(input, options).value ?? REGISTRATION_PLACEHOLDER;
}
